import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Carousel } from "react-bootstrap";
import { motion } from "framer-motion";
import { Pause, PlayArrow } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AccountBalance,
  AttachMoney,
  Business,
  CardTravel,
  Computer,
  Gavel,
  HomeWork,
  LocalHospital,
  School,
  Science,
  Store,
} from "@mui/icons-material";

import "../../styles/banner.css";

const industries = [
  { label: "Insurance", icon: <Business />, route: "insurance", color: "#1976d2" },
  { label: "Healthcare", icon: <LocalHospital />, route: "healthcare", color: "#e53935" },
  { label: "Education", icon: <School />, route: "education", color: "#8e24aa" },
  { label: "Small Industries", icon: <Store />, route: "small-industries", color: "#f57c00" },
  { label: "Research", icon: <Science />, route: "research", color: "#43a047" },
  { label: "Travel", icon: <CardTravel />, route: "travel", color: "#00acc1" },
  { label: "Real Estate", icon: <HomeWork />, route: "real-estate", color: "#6d4c41" },
  { label: "Finance", icon: <AttachMoney />, route: "finance", color: "#2e7d32" },
  { label: "Banking", icon: <AccountBalance />, route: "banking", color: "#3949ab" },
  { label: "Legal", icon: <Gavel />, route: "legal", color: "#5e35b1" },
  { label: "IT", icon: <Computer />, route: "it", color: "#546e7a" },
];

const Banner = () => {
  const [bannerData, setBannerData] = useState([]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dynamicData, setDynamicData] = useState({});
  const [activePulse, setActivePulse] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 300, height: 300 });
  const navigate = useNavigate();

  const centerX = size.width / 2;
  const centerY = size.height / 2;
  const radius = Math.min(size.width, size.height) / 2.5;
  const centerCircleRadius = Math.min(size.width, size.height) / 8;

  useEffect(() => {
    let mounted = true;

    const fetchBanners = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/fetchbannermeta`
        );
        if (mounted) setBannerData(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setBannerData([]);
      }
    };

    const getDynamicData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/getdynamicdata`
        );
        const data = res.data.reduce((acc, { fieldname, fieldvalue }) => {
          acc[fieldname] = fieldvalue;
          return acc;
        }, {});
        if (mounted) setDynamicData(data);
      } catch {
        if (mounted) setDynamicData({});
      }
    };

    fetchBanners();
    getDynamicData();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const updateSize = () => {
      const width = containerRef.current?.clientWidth || 300;
      setSize({ width, height: width });
    };

    updateSize();
    window.addEventListener("resize", updateSize);

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    if (isHovering) return undefined;

    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * industries.length);
      setActivePulse(randomIndex);
      setTimeout(() => setActivePulse(null), 900);
    }, 1800);

    return () => clearInterval(interval);
  }, [isHovering]);

  const slides = useMemo(() => {
    const sortedSlides = [...bannerData].sort((a, b) => a.position - b.position);
    return sortedSlides.length
      ? sortedSlides
      : [{ id: "industry-hub", showRadialHub: true }];
  }, [bannerData]);

  const renderRadialHub = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.55 }}
      className="banner-hub-motion"
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          aspectRatio: "1 / 1",
          maxWidth: { xs: 300, md: 420 },
          mx: "auto",
        }}
      >
        <svg width="100%" height="100%" viewBox={`0 0 ${size.width} ${size.height}`}>
          <circle cx={centerX} cy={centerY} r={centerCircleRadius} fill="#0f766e" />
          <text
            x={centerX}
            y={centerY + 5}
            textAnchor="middle"
            fontSize="14"
            fill="white"
            fontWeight="bold"
          >
            UDYOG
          </text>

          {industries.map((item, index) => {
            const angle = (2 * Math.PI * index) / industries.length;
            const outerX = centerX + radius * Math.cos(angle);
            const outerY = centerY + radius * Math.sin(angle);
            const lineStartX = centerX + centerCircleRadius * Math.cos(angle);
            const lineStartY = centerY + centerCircleRadius * Math.sin(angle);
            const circleRadius = size.width < 360 ? 20 : 26;

            return (
              <g
                key={item.route}
                className={`hub-item ${activePulse === index ? "active" : ""}`}
                onClick={() => navigate(`/2.0/industry/${item.route}`)}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onTouchStart={() => setIsHovering(true)}
                onTouchEnd={() => setTimeout(() => setIsHovering(false), 1500)}
              >
                <line
                  x1={lineStartX}
                  y1={lineStartY}
                  x2={outerX}
                  y2={outerY}
                  className="hub-line"
                />
                <circle
                  cx={outerX}
                  cy={outerY}
                  r={circleRadius}
                  fill={item.color}
                  className="hub-circle"
                />
                <foreignObject x={outerX - 12} y={outerY - 12} width={24} height={24}>
                  <div className="hub-icon">{item.icon}</div>
                </foreignObject>
                <text x={outerX} y={outerY - 32} textAnchor="middle" className="hub-label">
                  {item.label}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </motion.div>
  );

  return (
    <div className="banner-wrap">
      <div className="banner-glow banner-glow-one" />
      <div className="banner-glow banner-glow-two" />

      <div className="banner-header">
        <span className="banner-pill">
          {dynamicData.header || "Welcome to Udyog Connect"}
        </span>

        <h1>
          {dynamicData.headersub ||
            "Empowering communities by connecting people who need help with those who can offer it."}
        </h1>
      </div>

      <div className="banner-carousel-wrapper">
        <Carousel
          interval={isPlaying ? 5000 : null}
          controls={slides.length > 1}
          indicators={false}
          activeIndex={activeIndex}
          onSelect={(i) => setActiveIndex(i)}
        >
          {slides.map((slide, i) => (
            <Carousel.Item key={slide.id || i}>
              <div className="banner-card">
                {slide.showRadialHub ? (
                  <div className="banner-hub">{renderRadialHub()}</div>
                ) : (
                  <>
                    <div
                      className="banner-image"
                      style={{
                        "--bg": `url(${process.env.REACT_APP_API_BASE_URL}/api/admin/strembanner/${slide.id}/image)`,
                      }}
                    >
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/api/admin/strembanner/${slide.id}/image`}
                        alt={slide.title || "Udyog Connect banner"}
                      />
                    </div>

                    {slide.description && (
                      <div className="banner-content">
                        <p>{slide.description}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Carousel.Item>
          ))}
        </Carousel>

        <div className="banner-controls">
          <button type="button" onClick={() => setIsPlaying(!isPlaying)}>
            {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            {isPlaying ? "Pause" : "Play"}
          </button>

          {slides.length > 1 && (
            <div className="banner-dots">
              {slides.map((slide, idx) => (
                <button
                  type="button"
                  aria-label={`Show banner ${idx + 1}`}
                  key={slide.id || idx}
                  className={activeIndex === idx ? "active" : ""}
                  onClick={() => setActiveIndex(idx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Banner;
